import questActionSound from "./quest_actions.mp3";
import questOpenSound from "./quest_open.mp3";

const STORAGE_KEYS = {
  muted: "questxp:sound:muted",
  volume: "questxp:sound:volume",
};

const listeners = new Set();
const activeAssetSounds = new Set();
const assetSoundCache = new Map();

let audioContext = null;
let masterGain = null;
let primed = false;
let suppressPlaybackUntil = 0;

const defaultPreferences = {
  muted: false,
  volume: 0.72,
};

let preferences = { ...defaultPreferences };

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

function canUseBrowserAudio() {
  return typeof window !== "undefined" && typeof window.AudioContext !== "undefined";
}

function canUseHtmlAudio() {
  return typeof window !== "undefined" && typeof window.Audio !== "undefined";
}

function loadPreferences() {
  if (typeof window === "undefined") return;

  const mutedRaw = window.localStorage.getItem(STORAGE_KEYS.muted);
  const volumeRaw = window.localStorage.getItem(STORAGE_KEYS.volume);

  preferences = {
    muted: mutedRaw === null ? defaultPreferences.muted : mutedRaw === "true",
    volume:
      volumeRaw === null
        ? defaultPreferences.volume
        : clamp(Number(volumeRaw) || defaultPreferences.volume, 0, 1),
  };
}

function persistPreferences() {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEYS.muted, String(preferences.muted));
  window.localStorage.setItem(STORAGE_KEYS.volume, String(preferences.volume));
}

function emitPreferences() {
  const snapshot = { ...preferences };
  listeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (error) {
      console.error("Sound listener failed:", error);
    }
  });
}

function getAssetVolume() {
  return preferences.muted ? 0 : clamp(preferences.volume, 0, 1) * 0.68;
}

function syncActiveAssetVolumes() {
  activeAssetSounds.forEach((audio) => {
    audio.muted = preferences.muted;
    audio.volume = getAssetVolume();
  });
}

function ensureAudioContext() {
  if (!canUseBrowserAudio()) return null;
  if (audioContext) return audioContext;

  audioContext = new window.AudioContext();
  masterGain = audioContext.createGain();
  masterGain.connect(audioContext.destination);
  applyMasterGain();
  return audioContext;
}

function applyMasterGain() {
  if (!audioContext || !masterGain) return;
  const targetGain = preferences.muted ? 0 : clamp(preferences.volume, 0, 1) * 0.18;
  masterGain.gain.setTargetAtTime(targetGain, audioContext.currentTime, 0.015);
}

async function resumeAudioContext() {
  const ctx = ensureAudioContext();
  if (!ctx) return null;
  if (ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch (error) {
      return null;
    }
  }
  return ctx;
}

function attachPrimeListeners() {
  if (primed || typeof window === "undefined") return;
  primed = true;

  const unlock = () => {
    suppressPlaybackUntil = Date.now() + 400;
    resumeAudioContext();
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    window.removeEventListener("touchstart", unlock);
  };

  window.addEventListener("pointerdown", unlock, { passive: true, once: true });
  window.addEventListener("keydown", unlock, { passive: true, once: true });
  window.addEventListener("touchstart", unlock, { passive: true, once: true });
}

function getAssetAudio(src) {
  if (!canUseHtmlAudio() || !src) return null;
  if (assetSoundCache.has(src)) return assetSoundCache.get(src);

  const audio = new window.Audio(src);
  audio.preload = "auto";
  audio.muted = preferences.muted;
  audio.volume = getAssetVolume();
  assetSoundCache.set(src, audio);
  return audio;
}

function trackAssetPlayback(audio) {
  if (!audio) return;
  activeAssetSounds.add(audio);

  const cleanup = () => {
    activeAssetSounds.delete(audio);
    audio.removeEventListener("ended", cleanup);
    audio.removeEventListener("pause", cleanup);
    audio.removeEventListener("error", cleanup);
  };

  audio.addEventListener("ended", cleanup, { once: true });
  audio.addEventListener("pause", cleanup, { once: true });
  audio.addEventListener("error", cleanup, { once: true });
}

async function playAssetSound(src) {
  const baseAudio = getAssetAudio(src);
  if (!baseAudio) return false;

  const nextAudio = baseAudio.cloneNode(true);
  nextAudio.muted = preferences.muted;
  nextAudio.volume = getAssetVolume();
  nextAudio.currentTime = 0;
  trackAssetPlayback(nextAudio);

  try {
    await nextAudio.play();
    return true;
  } catch (error) {
    activeAssetSounds.delete(nextAudio);
    return false;
  }
}

function scheduleTone(
  ctx,
  {
    start,
    duration,
    frequency,
    endFrequency = frequency,
    gain = 0.35,
    type = "sine",
    detune = 0,
  }
) {
  const oscillator = ctx.createOscillator();
  const envelope = ctx.createGain();

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, start);
  oscillator.frequency.linearRampToValueAtTime(endFrequency, start + duration);
  oscillator.detune.setValueAtTime(detune, start);

  envelope.gain.setValueAtTime(0.0001, start);
  envelope.gain.linearRampToValueAtTime(gain, start + Math.min(0.025, duration * 0.35));
  envelope.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  oscillator.connect(envelope);
  envelope.connect(masterGain);

  oscillator.start(start);
  oscillator.stop(start + duration + 0.02);
}

const PATTERNS = {
  uiClick(ctx, start) {
    scheduleTone(ctx, { start, duration: 0.07, frequency: 720, endFrequency: 860, gain: 0.18, type: "triangle" });
  },
  systemNotice(ctx, start) {
    scheduleTone(ctx, { start, duration: 0.12, frequency: 540, endFrequency: 680, gain: 0.22, type: "triangle" });
    scheduleTone(ctx, { start: start + 0.1, duration: 0.18, frequency: 740, endFrequency: 920, gain: 0.2, type: "sine" });
  },
  systemPenalty(ctx, start) {
    scheduleTone(ctx, { start, duration: 0.18, frequency: 310, endFrequency: 210, gain: 0.26, type: "sawtooth" });
    scheduleTone(ctx, { start: start + 0.16, duration: 0.22, frequency: 260, endFrequency: 150, gain: 0.22, type: "square" });
  },
  questStart(ctx, start) {
    scheduleTone(ctx, { start, duration: 0.09, frequency: 460, endFrequency: 520, gain: 0.16, type: "triangle" });
    scheduleTone(ctx, { start: start + 0.08, duration: 0.14, frequency: 620, endFrequency: 760, gain: 0.18, type: "sine" });
  },
  questComplete(ctx, start) {
    scheduleTone(ctx, { start, duration: 0.08, frequency: 620, endFrequency: 740, gain: 0.18, type: "triangle" });
    scheduleTone(ctx, { start: start + 0.08, duration: 0.12, frequency: 780, endFrequency: 1040, gain: 0.22, type: "sine" });
    scheduleTone(ctx, { start: start + 0.18, duration: 0.16, frequency: 980, endFrequency: 1180, gain: 0.18, type: "triangle" });
  },
  questFailure(ctx, start) {
    scheduleTone(ctx, { start, duration: 0.12, frequency: 240, endFrequency: 180, gain: 0.22, type: "square" });
    scheduleTone(ctx, { start: start + 0.14, duration: 0.18, frequency: 210, endFrequency: 140, gain: 0.24, type: "sawtooth" });
  },
  bossReady(ctx, start) {
    scheduleTone(ctx, { start, duration: 0.18, frequency: 280, endFrequency: 340, gain: 0.22, type: "sawtooth" });
    scheduleTone(ctx, { start: start + 0.16, duration: 0.22, frequency: 420, endFrequency: 520, gain: 0.2, type: "triangle" });
    scheduleTone(ctx, { start: start + 0.3, duration: 0.26, frequency: 540, endFrequency: 760, gain: 0.2, type: "sine" });
  },
  bossClaim(ctx, start) {
    scheduleTone(ctx, { start, duration: 0.1, frequency: 520, endFrequency: 620, gain: 0.2, type: "triangle" });
    scheduleTone(ctx, { start: start + 0.1, duration: 0.12, frequency: 720, endFrequency: 900, gain: 0.2, type: "sine" });
    scheduleTone(ctx, { start: start + 0.22, duration: 0.24, frequency: 940, endFrequency: 1220, gain: 0.24, type: "triangle" });
  },
  levelUp(ctx, start) {
    scheduleTone(ctx, { start, duration: 0.12, frequency: 360, endFrequency: 430, gain: 0.18, type: "triangle" });
    scheduleTone(ctx, { start: start + 0.1, duration: 0.14, frequency: 520, endFrequency: 660, gain: 0.2, type: "sine" });
    scheduleTone(ctx, { start: start + 0.24, duration: 0.2, frequency: 760, endFrequency: 980, gain: 0.22, type: "triangle" });
  },
  rankUp(ctx, start) {
    scheduleTone(ctx, { start, duration: 0.18, frequency: 260, endFrequency: 360, gain: 0.2, type: "sawtooth" });
    scheduleTone(ctx, { start: start + 0.16, duration: 0.18, frequency: 360, endFrequency: 520, gain: 0.22, type: "triangle" });
    scheduleTone(ctx, { start: start + 0.3, duration: 0.22, frequency: 520, endFrequency: 760, gain: 0.24, type: "sine" });
    scheduleTone(ctx, { start: start + 0.48, duration: 0.34, frequency: 780, endFrequency: 1180, gain: 0.26, type: "triangle" });
  },
};

const ASSET_SOUNDS = {
  questAction: questActionSound,
  questOpen: questOpenSound,
  uiClick: questActionSound,
  questStart: questActionSound,
  questComplete: questActionSound,
  bossReady: questActionSound,
  bossClaim: questActionSound,
};

loadPreferences();

export function primeSoundSystem() {
  attachPrimeListeners();
  Object.values(ASSET_SOUNDS).forEach((src) => {
    getAssetAudio(src);
  });
}

export function getSoundPreferences() {
  return { ...preferences };
}

export function subscribeToSoundPreferences(listener) {
  listeners.add(listener);
  listener({ ...preferences });
  return () => listeners.delete(listener);
}

export function setSoundMuted(nextMuted) {
  preferences = {
    ...preferences,
    muted: !!nextMuted,
  };
  persistPreferences();
  applyMasterGain();
  syncActiveAssetVolumes();
  emitPreferences();
}

export function setSoundVolume(nextVolume) {
  preferences = {
    ...preferences,
    volume: clamp(Number(nextVolume) || 0, 0, 1),
  };
  persistPreferences();
  applyMasterGain();
  syncActiveAssetVolumes();
  emitPreferences();
}

export async function playSystemSound(name) {
  if (!name || preferences.muted) return false;

  if (Date.now() < suppressPlaybackUntil) {
    return false;
  }

  const assetSrc = ASSET_SOUNDS[name];
  if (assetSrc) {
    return playAssetSound(assetSrc);
  }

  const pattern = PATTERNS[name];
  if (!pattern) return false;

  const ctx = await resumeAudioContext();
  if (!ctx || !masterGain) return false;

  const start = ctx.currentTime + 0.01;
  try {
    pattern(ctx, start);
    return true;
  } catch (error) {
    console.error("System sound playback failed:", error);
    return false;
  }
}
